import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Flex } from 'rebass/styled-components';
import { Loading, ResultsEmpty } from '..';
import { useAppState } from '../../hooks';
import PolicySection from './PolicySection';
import PolicyConditionSection from './v2/policy-condition-section';
import EditorName from '../Editor/EditorName';
import PolicyException from './PolicyException';
import api from '../../api';

function PolicyEditor(props) {
  const {
    isEditing,
    temporaryPolicy,
    addNotification,
    brandId,
    validateObject,
    includeSdeAttribute,
    error,
    isComplexConditionEditor,
    onClickAdvancedCondition,
    onClickSimpleCondition,
    setTemporaryPolicy,
  } = props;

  const {
    accountId,
    isCcsV2,
    isLoading: isLoadingAppState,
  } = useAppState();

  const [isLoading, setIsLoading] = useState(true);
  const [customAttributes, setCustomAttributes] = useState(null);
  const [skills, setSkills] = useState(null);
  const [agents, setAgents] = useState(null);
  const [intents, setIntents] = useState(null);
  const [channels, setChannels] = useState(null);
  const isMounted = useRef(true);

  const { features } = useAppState();

  const fetchAgents = async () => {
    try {
      const data = await api.automationLookup.getAgents(brandId);
      if (isMounted.current) {
        setAgents(data);
      }
    } catch (err) {
      setAgents([]);
      addNotification('Error fetching agents', 'error');
    }
  };

  const fetchChannels = async () => {
    try {
      const data = api.getChannels();
      if (isMounted.current) {
        setChannels(data);
      }
    } catch (err) {
      setChannels([]);
      addNotification('Error fetching channels', 'error');
    }
  };

  const fetchSkills = async () => {
    try {
      const data = await api.automationLookup.getSkills(brandId);
      if (isMounted.current) {
        // NOTE: The skillids are returned as numbers, when they shuld be returned as strings.This
        // This is a workaround to prevent a backend change
        const dataWithFixedSkillIDs = data.map((item) => ({ ...item, id: `${item.id}` }));

        setSkills(dataWithFixedSkillIDs);
      }
    } catch (err) {
      setSkills([]);
      addNotification('Error fetching skills', 'error');
    }
  };

  const fetchIntents = async () => {
    try {
      const data = await api.account.getIntents(brandId);
      if (isMounted.current) {
        setIntents(data);
      }
    } catch (err) {
      setIntents([]);
      addNotification('Error fetching intents', 'error');
    }
  };

  const fetchSystemContext = async () => {
    try {
      const data = isCcsV2
        ? await api.ccsV2.getSystemContext(brandId, 'conversation')
        : await api.mavenContext.getSystemContext(brandId, 'conversation');
      return data;
    } catch (err) {
      // There is currently no implementation to distinguish between a brand that does not exist,
      // and a brand that has no custom attributes yet... so for the moment, we ignore this error
      if (err.response && err.response.status !== 404) {
        addNotification(err, 'error');
      }
      return [];
    }
  };

  const fetchSdeContext = async () => {
    if (!includeSdeAttribute) {
      return [];
    }
    try {
      const sdeAttributes = [];
      const data = isCcsV2
        ? await api.ccsV2.getSdeList(brandId)
        : await api.mavenContext.getSdeList(brandId);
      data.forEach((sdeData) => (
        sdeAttributes.push(sdeData.attribute)
      ));
      return sdeAttributes;
    } catch (err) {
      if (err.response && err.response.status !== 404) {
        addNotification(err, 'error');
      }
      return [];
    }
  };

  const fetchCustomContexts = async () => {
    try {
      const data = isCcsV2
        ? await api.ccsV2.getCustomContexts(brandId)
        : await api.mavenConfig.getCustomContexts(brandId);

      return data;
    } catch (err) {
      // There is currently no implementation to distinguish between a brand that does not exist,
      // and a brand that has no custom attributes yet... so for the moment, we ignore this error
      if (err.response && err.response.status !== 404) {
        addNotification(err, 'error');
      }
      return [];
    }
  };

  const fetchOrchestratorContext = async () => {
    try {
      const data = api.getOrchestratorContext(brandId);

      return data;
    } catch (err) {
      if (err.response && err.response.status !== 404) {
        addNotification(err, 'error');
      }
      return [];
    }
  };

  const fetchCustom = async () => {
    const [conversationData, customData, sdeData, orchestratorData] = await Promise.all([
      fetchSystemContext(),
      fetchCustomContexts(),
      fetchSdeContext(),
      fetchOrchestratorContext(),
    ]);

    // TODO: v2 schema is different here. Uses `varName` instead of name
    // consider writing some adapter so we don't have a lot of this type of
    // code scattered around.
    const customNames = customData.map((item) => `custom.${item.name || item.varName}`);

    if (isMounted.current) {
      setCustomAttributes(
        customNames.concat(conversationData).concat(orchestratorData).concat(sdeData),
      );
    }
  };

  const getCustomAttributes = async () => {
    if (isMounted.current) {
      setIsLoading(true);
    }

    // Fetch all data in parallel to reduce wait time
    await Promise.all([
      fetchAgents(),
      fetchSkills(),
      fetchCustom(),
      fetchIntents(),
      fetchChannels(),
    ]);

    if (isMounted.current) {
      setIsLoading(false);
    }
  };

  const getException = () => ({ conditions: [], actions: [], ...temporaryPolicy.exception });

  const setException = (exception) => {
    const newPolicy = { ...temporaryPolicy };
    newPolicy.exception = exception;
    validateObject(newPolicy);
  };

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []); // eslint-disable-line

  useEffect(() => {
    async function fetchData() {
      await getCustomAttributes();
    }
    if (accountId && !isLoadingAppState) {
      fetchData();
    }
  }, [isLoadingAppState, accountId]); // eslint-disable-line

  // A little hacky. Consider a more robust solution later.
  // we need to have a doc id to execute delete.
  if (temporaryPolicy && temporaryPolicy.docId && error) {
    return (
      <ResultsEmpty text={error} />
    );
  }

  // wait until all api calls have finished before showing editor
  if (isLoading
    || agents === null
    || intents === null
    || skills === null
    || customAttributes === null
    || channels === null
  ) {
    return (
      <Flex align="center" justifyContent="center" py={3}>
        <Loading size={24} data-testid="loading-policy" />
      </Flex>
    );
  }

  return (
    <Box mb={2} data-testid="policy-editor-container">
      <EditorName
        disabled={!isEditing}
        isEditing={isEditing}
        temporaryObject={temporaryPolicy}
        validateObject={validateObject}
      />
      {features['maven.feature.textareaEditor'] && isCcsV2 ? ( // check switch for complex policies
        <PolicyConditionSection
          temporaryPolicy={temporaryPolicy}
          isEditing={isEditing}
          customAttributes={customAttributes}
          intents={intents}
          channels={channels}
          validateObject={validateObject}
          isComplexConditionEditor={isComplexConditionEditor}
          onClickAdvancedCondition={onClickAdvancedCondition}
          onClickSimpleCondition={onClickSimpleCondition}
          setTemporaryPolicy={setTemporaryPolicy}
        />
      ) : (
        <PolicySection
          sectionType="conditions"
          temporaryPolicy={temporaryPolicy}
          isEditing={isEditing}
          intents={intents}
          customAttributes={customAttributes}
          channels={channels}
          validateObject={validateObject}
        />
      )}
      <PolicySection
        sectionType="actions"
        temporaryPolicy={temporaryPolicy}
        isEditing={isEditing}
        skills={skills}
        agents={agents}
        customAttributes={customAttributes}
        validateObject={validateObject}
      />
      {!isCcsV2 && (
        <PolicyException
          isEditing={isEditing}
          setException={setException}
          exception={getException()}
          skills={skills}
          agents={agents}
          intents={intents}
          customAttributes={customAttributes}
        />
      )}
    </Box>
  );
}

PolicyEditor.defaultProps = {
  brandId: undefined,
  isEditing: false,
  temporaryPolicy: {},
  error: undefined,
  isComplexConditionEditor: false,
  onClickAdvancedCondition: () => { },
  onClickSimpleCondition: () => { },
  setTemporaryPolicy: () => { },
};

PolicyEditor.propTypes = {
  brandId: PropTypes.string,
  isEditing: PropTypes.bool,
  includeSdeAttribute: PropTypes.bool.isRequired,
  temporaryPolicy: PropTypes.object,
  addNotification: PropTypes.func.isRequired,
  validateObject: PropTypes.func.isRequired,
  error: PropTypes.string,
  isComplexConditionEditor: PropTypes.bool,
  onClickAdvancedCondition: PropTypes.func,
  onClickSimpleCondition: PropTypes.func,
  setTemporaryPolicy: PropTypes.func,
};

export default PolicyEditor;
